'use client';
import { SanitizedHtml } from '@/components/sanitizedHtml';
import { connectedField } from '@/redux/fields';
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Grid, Typography } from '@mui/material';
import { reviewAndSendLink, reviewAndSubmit, variables } from '@repo/onb-api';
import { Checkbox, MaterialIcon } from '@repo/ui';
import { formatter } from '@repo/utils';
import { useId, useRef, useState } from 'react';
import { OnbForm } from '../form';

const RowDetails = ({ label, value }: { label?: string; value?: string }): React.JSX.Element => {
  return (
    <Grid
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        fontWeight: label === 'CFTTEA' ? 'bold' : 400,
      }}
    >
      <Typography variant="body3">{label}</Typography>
      <Typography variant="body3">{value}</Typography>
    </Grid>
  );
};

type ConfirmationDetailsProps = variables & {
  stepName: reviewAndSubmit['name'] | reviewAndSendLink['name'];
  readOnly?: boolean;
  submitLabel?: string;
  showSujetoObligado?: boolean;
};

export const ConfirmationDetails = ({
  termsAndConditions,
  requestedAmount,
  installmentCount,
  paymentSchedule,
  documentNumber,
  gender,
  fullName,
  birthDate,
  taxpayerId,
  email,
  mobilePhoneNumber,
  province,
  paymentMethod,
  bankAccountReference,
  installmentRates,
  stepName,
  readOnly,
  submitLabel,
  showSujetoObligado,
}: ConfirmationDetailsProps): React.JSX.Element => {
  const [accordionState, setAccordionState] = useState([false, false, false]);
  const [checkboxState, setCheckboxState] = useState([false, false, false]);

  const baseId = useId();
  const termsTextId = (index: number) => `${baseId}-terms-${index}`;
  const termsErrorId = (index: number) => `${baseId}-terms-error-${index}`;

  const checkboxRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [checkboxOneError, checkboxTwoError, checkboxThreeError] = checkboxRefs.current.map(
    (ref) => ref?.className.includes('checkboxError') ?? false,
  );

  // Extra top space separates the terms group from the account details above
  return (
    <Grid container sx={{ flexDirection: 'column', flexWrap: 'nowrap', height: '100%', gap: 2, mt: 2 }}>
      <Divider />
      <OnbForm<reviewAndSubmit>
        stepName={stepName!}
        submitLabel={submitLabel || 'Confirmar'}
        submitBlacklist={['termsAndConditions']}
      >
        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, border: 'none' }}
          expanded={accordionState[0]}
          onChange={() => setAccordionState([!accordionState[0], accordionState[1]!, accordionState[2]!])}
        >
          <AccordionSummary
            sx={{ p: 0 }}
            expandIcon={<MaterialIcon icon="keyboard_arrow_down" size={34} color="primary" />}
          >
            {!readOnly && (
              <Checkbox
                ref={(el) => {
                  checkboxRefs.current[0] = el;
                }}
                // Action inside AccordionSummary: keep clicks/focus from toggling the accordion
                onFocus={(e) => e.stopPropagation()}
                slotProps={{
                  input: {
                    'aria-labelledby': termsTextId(0),
                    'aria-describedby': !checkboxState[0] && checkboxOneError ? termsErrorId(0) : undefined,
                  },
                }}
                forceIsChecked={checkboxState[0]}
                sx={{ p: 0, maxWidth: '160px' }}
                {...connectedField({
                  checkbox: true,
                  stepName,
                  name: 'hasTermsAccepted',
                  required: true,
                  beforeChange: (value) => {
                    setCheckboxState([!!value, !!checkboxState[1], !!checkboxState[2]]);
                    return !!value && checkboxState[1] && checkboxState[2];
                  },
                  errorHandler: {
                    type: 'checkbox',
                  },
                })}
              />
            )}
            <Typography
              id={termsTextId(0)}
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 1,
                width: '70%',
              }}
            >
              Detalles de la operación
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Grid container sx={{ flexDirection: 'column' }}>
              <RowDetails label="Préstamo elegido" value={formatter(`${requestedAmount}`, 'currency')} />
              <RowDetails label="Cantidad de cuotas" value={`${installmentCount}`} />
              <Typography variant="body3" sx={{ fontWeight: '500', mt: 2 }}>
                Vencimientos
              </Typography>
              {paymentSchedule?.map(({ number, dueDate, amount }) => (
                <RowDetails
                  key={number}
                  label={formatter(`${dueDate}`, 'date')}
                  value={formatter(`${amount}`, 'currency')}
                />
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
        {!checkboxState[0] && checkboxOneError && (
          <Typography id={termsErrorId(0)} role="alert" variant="body2" sx={{ textAlign: 'center' }} color="error">
            Marcá la casilla para continuar
          </Typography>
        )}
        <Divider />
        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
          expanded={accordionState[1]}
          onChange={() => setAccordionState([accordionState[0]!, !accordionState[1], accordionState[2]!])}
        >
          <AccordionSummary
            sx={{ p: 0 }}
            expandIcon={<MaterialIcon icon="keyboard_arrow_down" size={34} color="primary" />}
          >
            {!readOnly && (
              <Checkbox
                ref={(el) => {
                  checkboxRefs.current[1] = el;
                }}
                // Action inside AccordionSummary: keep clicks/focus from toggling the accordion
                onFocus={(e) => e.stopPropagation()}
                slotProps={{
                  input: {
                    'aria-labelledby': termsTextId(1),
                    'aria-describedby': !checkboxState[1] && checkboxTwoError ? termsErrorId(1) : undefined,
                  },
                }}
                forceIsChecked={checkboxState[1]}
                sx={{ p: 0 }}
                {...connectedField({
                  checkbox: true,
                  stepName,
                  name: 'hasTermsAccepted',
                  required: true,
                  beforeChange: (value) => {
                    setCheckboxState([!!checkboxState[0], !!value, !!checkboxState[2]]);
                    return !!checkboxState[0] && !!value && !!checkboxState[2];
                  },
                  errorHandler: {
                    type: 'checkbox',
                  },
                })}
              />
            )}
            <Typography
              id={termsTextId(1)}
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 1,
                width: '70%',
              }}
            >
              Conocé los detalles de tu préstamo
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Grid container sx={{ flexDirection: 'column' }}>
              <Typography variant="body3" sx={{ fontWeight: '500' }}>
                Tus datos
              </Typography>
              <RowDetails label="Nombre y apellido" value={fullName} />
              <RowDetails label="Número de documento" value={documentNumber} />
              <RowDetails label="Género" value={gender === 'MALE' ? 'Masculino' : 'Femenino'} />
              <RowDetails label="Fecha de nacimiento" value={formatter(`${birthDate}`, 'date')} />
              <RowDetails label="ID Tributaria" value={taxpayerId} />
              <RowDetails label="Categoría IVA" value={'Consumidor Final'} />
              <RowDetails label="Provincia" value={formatter(`${province}`, 'capitalize')} />
              <RowDetails label="Email" value={email} />
              <RowDetails label="Celular" value={mobilePhoneNumber} />
              <Typography variant="body3" sx={{ fontWeight: '500', mt: 2 }}>
                Condiciones de tu préstamo
              </Typography>
              <RowDetails label="Capital" value={formatter(`${requestedAmount}`, 'currency')} />
              <RowDetails label="Moneda" value={'Pesos'} />
              <RowDetails label="Cantidad de cuotas" value={`${installmentCount}`} />
              <RowDetails
                label="Valor de la cuota"
                value={`${formatter(paymentSchedule?.[0]?.amount || 0, 'currency')}`}
              />
              <RowDetails label="Sistema de amortización" value={'Francés'} />
              <RowDetails label="Monto neto" value={formatter(`${requestedAmount}`, 'currency')} />
              {Object.entries(installmentRates || {}).map(([key, value]) => (
                <RowDetails key={key} label={key} value={`${formatter(value, 'number')}%`} />
              ))}
              <RowDetails
                label="Método de pago"
                value={paymentMethod === 'DEBIT_CARD' ? 'Tarjeta' : 'En locales habilitados o Pago Digital'}
              />
              <RowDetails label="Comisión por precancelación" value="9%+IVA" />
              {bankAccountReference && (
                <>
                  <Typography variant="body3" sx={{ fontWeight: '500', mt: 2 }}>
                    Forma de acreditación
                  </Typography>
                  <RowDetails label="Cuenta" value={bankAccountReference} />
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
        {!checkboxState[1] && checkboxTwoError && (
          <Typography id={termsErrorId(1)} role="alert" variant="body2" sx={{ textAlign: 'center' }} color="error">
            Marcá la casilla para continuar
          </Typography>
        )}
        <Divider />
        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' } }}
          expanded={accordionState[2]}
          onChange={() => setAccordionState([accordionState[0]!, accordionState[1]!, !accordionState[2]])}
        >
          <AccordionSummary
            sx={{ p: 0 }}
            expandIcon={<MaterialIcon icon="keyboard_arrow_down" size={34} color="primary" />}
          >
            {!readOnly && (
              <Checkbox
                ref={(el) => {
                  checkboxRefs.current[2] = el;
                }}
                // Action inside AccordionSummary: keep clicks/focus from toggling the accordion
                onFocus={(e) => e.stopPropagation()}
                slotProps={{
                  input: {
                    'aria-labelledby': termsTextId(2),
                    'aria-describedby': !checkboxState[2] && checkboxThreeError ? termsErrorId(2) : undefined,
                  },
                }}
                forceIsChecked={checkboxState[2]}
                sx={{ p: 0 }}
                {...connectedField({
                  checkbox: true,
                  stepName,
                  name: 'hasTermsAccepted',
                  required: true,
                  beforeChange: (value) => {
                    setCheckboxState([!!checkboxState[0], !!checkboxState[1], !!value]);
                    return !!checkboxState[0] && !!checkboxState[1] && !!value;
                  },
                  errorHandler: {
                    type: 'checkbox',
                  },
                })}
              />
            )}
            <Typography
              id={termsTextId(2)}
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 1,
                width: '70%',
              }}
            >
              Términos y condiciones de tu préstamo
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Typography variant="body3">
              <SanitizedHtml html={termsAndConditions} />
            </Typography>
          </AccordionDetails>
        </Accordion>
        {!checkboxState[2] && checkboxThreeError && (
          <Typography id={termsErrorId(2)} role="alert" variant="body2" sx={{ textAlign: 'center' }} color="error">
            Marcá la casilla para continuar
          </Typography>
        )}
        <Divider />
        {accordionState?.length && accordionState?.some((value) => value === true) && (
          <Button variant="text" onClick={() => setAccordionState([false, false, false])}>
            Mostrar menos
          </Button>
        )}
        {showSujetoObligado && (
          <Typography variant="body3" sx={{ mb: 2 }}>
            Al confirmar declara que NO se encuentra incluido y/o alcanzado por la “Nómina de Personas Expuestas
            Políticamente” aprobada por resolución UIF 35/2023, sus complementarias y/o modificatorias, que desde ya
            declara conocer y de acuerdo a lo establecido en el art. 20 de la ley 25.246 declara que NO es Sujeto
            Obligado. Para mayor información visite https://www.argentina.gob.ar/uif
          </Typography>
        )}
      </OnbForm>
    </Grid>
  );
};
